import {useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner, Table,  Tabs, Tab} from 'react-bootstrap'
import {useMutation, useQuery} from '@apollo/client'
import axios from 'axios'
import {toast} from 'react-toastify'
import {UPDATE_BOTMAKER_TEMPLATE_CONFIG} from '../../gql/mutations/botmaker'
import {GET_BOTMAKER_VARIABLE_OPTIONS} from '../../gql/queries/botmaker'

type WhatsAppTemplate = {
  id?: string
  name?: string
  body?: {text?: string}
  allowedModules?: string[]
  variableMapping?: Record<string, string>
}

interface ITemplateConfigurationModalProps {
  show: boolean
  onHide: () => void
  template: WhatsAppTemplate
  onSuccess: () => void
}

// Hardcoded for now, but could be dynamic if needed
const MODULE_OPTIONS = [
  {value: 'leads', label: 'Leads'},
  {value: 'policies', label: 'Polizas/Cotizaciones'},
  {value: 'contacts', label: 'Contactos'},
]

export const TemplateConfigurationModal = ({
  show,
  onHide,
  template,
  onSuccess,
}: ITemplateConfigurationModalProps) => {
  const [loading, setLoading] = useState(false)
  // const [saving, setSaving] = useState(false)
  
  // State
  const [selectedModules, setSelectedModules] = useState<string[]>([])
  // Mapping can be flat (legacy) or nested: {'var': 'field'} OR {'leads': {'var': 'field'}, ...}
  // We'll manage it as `any` locally for flexibility, but try to normalize to module-nested if multiple modules are checked.
  const [mapping, setMapping] = useState<any>({})
  
  const [activeTab, setActiveTab] = useState<string>('')
  
  const [variableOptions, setVariableOptions] = useState<Record<string, {label: string, variables: {value: string, label: string}[]}>>({})
  const [templateVariables, setTemplateVariables] = useState<string[]>([])

  useEffect(() => {
    if (show && template) {
      // Init state from template
      setSelectedModules(template.allowedModules || [])
      setMapping(template.variableMapping || {})
      if (template.allowedModules && template.allowedModules.length > 0) {
          setActiveTab(template.allowedModules[0])
      } else {
          setActiveTab('leads')
      }
      
      
      // Parse variables from body
      let text = ''
      try {
        if (typeof template.body === 'string') {
           const parsedBody = JSON.parse(template.body)
           text = parsedBody.text || ''
        } else if (typeof template.body === 'object' && template.body !== null) {
           text = (template.body as any).text || ''
        }
      } catch (e) {
        console.error("Error parsing template body", e)
      }
      
      // Support both {{var}} and ${var} patterns
      // WhatsApp standard is usually {{1}}, Botmaker often uses ${name}
      // Pattern 1: {{content}}
      const regexDouble = /{{(.*?)}}/g;
      // Pattern 2: ${content}
      const regexDollar = /\${(.*?)}/g;
      
      const found: string[] = []
      let match;
      
      // Check {{...}}
      while ((match = regexDouble.exec(text)) !== null) {
          if(!found.includes(match[1])) {
            found.push(match[1].trim())
          }
      }
      // Check ${...}
      while ((match = regexDollar.exec(text)) !== null) {
          if(!found.includes(match[1])) {
            found.push(match[1].trim())
          }
      }
      setTemplateVariables(found)
      
      // Fetch options
      // fetchOptions() // No longer needed, useQuery handles it
    }
  }, [show, template])

  const {data: optionsData, loading: optionsLoading, error: optionsError} = useQuery(GET_BOTMAKER_VARIABLE_OPTIONS)

  useEffect(() => {
     if (optionsData?.botmakerVariableOptions) {
         const newOptions: Record<string, any> = {}
         optionsData.botmakerVariableOptions.forEach((item: any) => {
             newOptions[item.key] = {
                 label: item.label,
                 variables: item.variables
             }
         })
         setVariableOptions(newOptions)
     }
     if (optionsError) {
        console.error(optionsError)
        toast.error('Error fetching variable options')
     }
  }, [optionsData, optionsError])

  const [updateConfig, {loading: saving}] = useMutation(UPDATE_BOTMAKER_TEMPLATE_CONFIG)

  const handleSave = async () => {
    if (!template.id) return
    try {
      const {data} = await updateConfig({
        variables: {
          pTemplateId: template.id,
          allowedModules: selectedModules,
          variableMapping: mapping
        }
      })
      
      if (data?.updateBotmakerTemplateConfig?.success) {
        toast.success('Configuration saved')
        onSuccess()
        onHide()
      } else {
         toast.error(data?.updateBotmakerTemplateConfig?.message || 'Error saving configuration')
      }

    } catch (e: any) {
      console.error(e)
      toast.error(e.message || 'Error saving configuration')
    }
  }

  const toggleModule = (mod: string) => {
    if (selectedModules.includes(mod)) {
      setSelectedModules(selectedModules.filter(m => m !== mod))
    } else {
      setSelectedModules([...selectedModules, mod])
      if (!activeTab) setActiveTab(mod)
    }
  }

  // Helper to get/set mapping for current module (or flat)
  const getValueForVar = (v: string, moduleKey: string) => {
      // If mapping has module key, look inside
      if (mapping[moduleKey] && typeof mapping[moduleKey] === 'object') {
          return mapping[moduleKey][v] || ''
      }
      // Fallback: check if mapping is flat and applied (legacy)
      // BUT if we are in multi-module mode, we should favor nested.
      // If flat exists and we are moving to nested, we might want to migrate it?
      // For simplicity:
      if (typeof mapping[v] === 'string') return mapping[v] // Legacy flat
      
      return ''
  }

  const updateMapping = (v: string, val: string, moduleKey: string) => {
      setMapping((prev: any) => {
          const newMap = {...prev}
          
          // Ensure nested structure for this module
          if (!newMap[moduleKey] || typeof newMap[moduleKey] !== 'object') {
             // If it was mixing flat, we might lose flat data if we don't migrate.
             // Let's copy flat data to this module if it matches? 
             // Or just init empty
             newMap[moduleKey] = {}
          }
           newMap[moduleKey] = {
               ...newMap[moduleKey],
               [v]: val
           }
           return newMap
      })
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>Configure Template: {template.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='text-center py-4'>
            <Spinner animation='border' />
          </div>
        ) : (
          <div>
            {/* Modules Section */}
            <div className='mb-4'>
              <h5 className='fw-bold'>Allowed Modules</h5>
              <div className='text-muted small mb-2'>
                Select where this template can be used.
              </div>
              <div className='d-flex gap-3'>
                {MODULE_OPTIONS.map(opt => (
                  <Form.Check
                    key={opt.value}
                    type='checkbox'
                    id={`mod-${opt.value}`}
                    label={opt.label}
                    checked={selectedModules.includes(opt.value)}
                    onChange={() => toggleModule(opt.value)}
                  />
                ))}
              </div>
            </div>
            
            <hr />

            {/* Variables Section */}
            <div className='mb-3'>
              <h5 className='fw-bold'>Variable Mapping</h5>
              <div className='text-muted small mb-3'>
                {"Map the template variables (e.g. {{1}}, ${name}...) to system fields for each allowed module."}
              </div>
              
              {templateVariables.length === 0 ? (
                <div className='alert alert-info'>
                  No variables found in the template body.
                </div>
              ) : selectedModules.length === 0 ? (
                 <div className='alert alert-warning'>
                    Please select at least one module to configure mappings.
                 </div>
              ) : (
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k || '')}
                    className="mb-3"
                >
                    {selectedModules.map(mod => {
                        const moduleLabel = MODULE_OPTIONS.find(m => m.value === mod)?.label || mod
                        return (
                            <Tab eventKey={mod} title={moduleLabel} key={mod}>
                                <Table bordered hover size="sm">
                                  <thead>
                                    <tr className='bg-light'>
                                      <th style={{width: '20%'}}>Variable</th>
                                      <th>System Field ({moduleLabel})</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {templateVariables.map(v => (
                                      <tr key={v}>
                                        <td className='align-middle fw-bold text-center'>
                                          {v}
                                        </td>
                                        <td>
                                          <Form.Select
                                            value={getValueForVar(v, mod)}
                                            onChange={(e) => updateMapping(v, e.target.value, mod)}
                                          >
                                            <option value="">Select a field...</option>
                                            {Object.entries(variableOptions)
                                                .filter(([groupKey]) => groupKey === mod) // Filter by current module ('leads', 'policies'...)
                                                .map(([groupKey, group]) => {
                                                return (
                                                    <optgroup key={groupKey} label={group.label}>
                                                        {group.variables.map((opt: any) => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                )
                                            })}
                                          </Form.Select>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                            </Tab>
                        )
                    })}
                </Tabs>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={saving || loading}>
          {saving && <Spinner animation='border' size='sm' className='me-2' />}
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
